import React, { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Clock, MapPin, Scissors, CheckCircle2, ArrowRight } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import StepFrame, { StepTitle } from "../StepFrame";
import { BOOKING_DEFAULTS } from "../../../data/looks";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const TIMES = ["10:00 AM", "11:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM"];

export default function BookingStep() {
  const { selectedOutfit, selectedAccessories, bookingConfirmed, confirmBooking, goTo } = useHOH();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [openDate, setOpenDate] = useState(false);

  return (
    <StepFrame className="text-center">
      <StepTitle overline="Step 7" title="Book the look" subtitle="Reserve your styling session — outfit and accessories estimated separately." />

      {!bookingConfirmed ? (
        <div className="hoh-card p-8 md:p-10 grid md:grid-cols-2 gap-8 text-left">
          <div className="space-y-5">
            <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-xl bg-hoh-blush flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-hoh-rose" /></div><div><p className="text-xs text-hoh-slate">Recommended Salon</p><p className="font-semibold text-black">{BOOKING_DEFAULTS.salon}</p></div></div>
            <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-xl bg-hoh-lavender flex items-center justify-center shrink-0"><Scissors className="w-5 h-5 text-hoh-rose" /></div><div><p className="text-xs text-hoh-slate">Recommended Stylist</p><p className="font-semibold text-black">{BOOKING_DEFAULTS.stylist}</p></div></div>
            <div>
              <p className="text-xs text-hoh-slate mb-2">Services Required</p>
              <div className="flex flex-wrap gap-2">{["Hairstyle Service", "Grooming / Cosmetic Service", "Look Finishing"].map((s) => <span key={s} className="bg-hoh-cream rounded-full px-3 py-1 text-sm text-hoh-navy">{s}</span>)}</div>
            </div>
            <div className="space-y-2 pt-2 border-t border-black/5">
              <div className="flex justify-between text-sm"><span className="text-hoh-slate">Salon (services)</span><span className="font-semibold text-black">{BOOKING_DEFAULTS.price}</span></div>
              <div className="flex justify-between text-sm"><span className="text-hoh-slate">Outfit estimate</span><span className="font-semibold text-black">{selectedOutfit?.priceRange || "—"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-hoh-slate">Accessories</span><span className="font-semibold text-black">{selectedAccessories?.length ? `${selectedAccessories.length} items` : "—"}</span></div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-hoh-slate">Select Date</label>
              <Popover open={openDate} onOpenChange={setOpenDate}>
                <PopoverTrigger asChild>
                  <button className="mt-1 w-full hoh-card px-4 py-3 flex items-center gap-2 text-left" data-testid="date-picker-btn"><CalendarIcon className="w-4 h-4 text-hoh-rose" /><span className={date ? "text-black" : "text-hoh-slate"}>{date ? format(date, "PPP") : "Pick a date"}</span></button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setOpenDate(false); }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs text-hoh-slate">Select Time</label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger className="mt-1 rounded-2xl h-12" data-testid="time-select"><div className="flex items-center gap-2"><Clock className="w-4 h-4 text-hoh-rose" /><SelectValue placeholder="Pick a time" /></div></SelectTrigger>
                <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button onClick={confirmBooking} disabled={!date || !time} className="btn-royal w-full" data-testid="confirm-booking-btn">Confirm Booking</button>
          </div>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="hoh-card p-10 text-center" data-testid="booking-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-hoh-blush flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-hoh-rose" /></div>
          <h3 className="mt-6 text-4xl font-semibold text-black">Your occasion look is booked.</h3>
          <p className="mt-3 text-hoh-slate">{date && format(date, "PPP")} · {time} at {BOOKING_DEFAULTS.salon}</p>
          <button onClick={() => goTo(STEP.CONTENT)} className="btn-royal mt-8" data-testid="to-content-btn">Generate Content <ArrowRight className="w-4 h-4" /></button>
        </motion.div>
      )}
    </StepFrame>
  );
}
